import React, { forwardRef } from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'

const EMPTY_VALUE = '__learnly_empty_value__'

const normalizeValue = (value) => {
  if (value === '' || value === null) return EMPTY_VALUE
  return String(value)
}

const collectOptions = (children) =>
  React.Children.toArray(children).flatMap((child) => {
    if (!React.isValidElement(child)) return []
    if (child.type === React.Fragment) return collectOptions(child.props.children)
    if (child.type !== 'option') return []

    return [{
      disabled: Boolean(child.props.disabled),
      key: child.key ?? child.props.value,
      label: child.props.children,
      value: normalizeValue(child.props.value),
    }]
  })

const AppSelect = forwardRef(({
  children,
  className = '',
  defaultValue,
  disabled = false,
  id,
  name,
  onChange,
  required = false,
  style,
  value,
  ...triggerProps
}, ref) => {
  const options = collectOptions(children)
  const controlledValue = value === undefined ? undefined : normalizeValue(value)
  const initialValue = defaultValue === undefined ? undefined : normalizeValue(defaultValue)

  const handleValueChange = (nextValue) => {
    const resolvedValue = nextValue === EMPTY_VALUE ? '' : nextValue
    onChange?.({
      currentTarget: { id, name, value: resolvedValue },
      target: { id, name, value: resolvedValue },
    })
  }

  return (
    <SelectPrimitive.Root
      value={controlledValue}
      defaultValue={initialValue}
      disabled={disabled}
      name={name}
      required={required}
      onValueChange={handleValueChange}
    >
      <SelectPrimitive.Trigger
        ref={ref}
        id={id}
        className={`app-select-trigger ${className}`.trim()}
        style={style}
        {...triggerProps}
      >
        <SelectPrimitive.Value />
        <SelectPrimitive.Icon className="app-select-chevron">
          <ChevronDown size={16} aria-hidden="true" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          className="app-select-content"
          position="popper"
          sideOffset={6}
          collisionPadding={12}
        >
          <SelectPrimitive.Viewport className="app-select-viewport">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.key}
                value={option.value}
                disabled={option.disabled}
                className="app-select-option"
              >
                <SelectPrimitive.ItemIndicator className="app-select-indicator">
                  <Check size={15} strokeWidth={2.5} aria-hidden="true" />
                </SelectPrimitive.ItemIndicator>
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
})

AppSelect.displayName = 'AppSelect'

export default AppSelect
